import { ref, onUnmounted } from 'vue'

/**
 * 拖拽交互Hook
 * 支持：拖拽移动、拖拽调整大小、拖拽创建
 */
export function useDrag(options = {}) {
  const {
    onDragStart,
    onDragging,
    onDragEnd,
    onDrop,
    grid = 15 // 网格吸附
  } = options

  const isDragging = ref(false)
  const dragData = ref(null)
  const dragPosition = ref({ x: 0, y: 0 })

  // 拖拽开始
  function handleDragStart(e, data) {
    if (e.button !== 0) return // 只响应左键

    isDragging.value = true
    dragData.value = data

    // 设置拖拽数据
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', JSON.stringify(data))

    // 创建拖拽预览
    createDragPreview(e, data)

    // 添加拖拽样式
    document.body.classList.add('dragging')

    onDragStart?.(e, data)

    // 绑定全局事件
    document.addEventListener('dragover', handleDragOver)
    document.addEventListener('drop', handleDrop)
    document.addEventListener('dragend', handleDragEnd)
  }

  // 创建拖拽预览
  function createDragPreview(e, data) {
    const preview = document.createElement('div')
    preview.className = 'drag-preview'
    preview.innerHTML = `
      <div class="bg-white rounded-lg shadow-lg p-3 border border-slate-200 opacity-80">
        <p class="text-sm font-medium text-slate-900">${data.title || '日程'}</p>
        <p class="text-xs text-slate-500">${data.time || ''}</p>
      </div>
    `
    preview.style.position = 'absolute'
    preview.style.pointerEvents = 'none'
    preview.style.zIndex = '9999'
    document.body.appendChild(preview)

    e.dataTransfer.setDragImage(preview, 0, 0)

    // 清理预览元素
    setTimeout(() => preview.remove(), 0)
  }

  // 拖拽经过
  function handleDragOver(e) {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'

    const x = Math.round(e.clientX / grid) * grid
    const y = Math.round(e.clientY / grid) * grid
    dragPosition.value = { x, y }

    onDragging?.(e, { x, y })
  }

  // 拖拽放置
  function handleDrop(e) {
    e.preventDefault()

    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'))
      const rect = e.currentTarget.getBoundingClientRect()

      const dropData = {
        ...data,
        clientX: e.clientX,
        clientY: e.clientY,
        offsetX: e.clientX - rect.left,
        offsetY: e.clientY - rect.top
      }

      onDrop?.(e, dropData)
    } catch (err) {
      console.error('Drop data parse error:', err)
    }
  }

  // 拖拽结束
  function handleDragEnd(e) {
    isDragging.value = false
    dragData.value = null
    document.body.classList.remove('dragging')

    // 移除全局事件
    document.removeEventListener('dragover', handleDragOver)
    document.removeEventListener('drop', handleDrop)
    document.removeEventListener('dragend', handleDragEnd)

    onDragEnd?.(e)
  }

  // 清理
  function cleanup() {
    document.body.classList.remove('dragging')
    document.removeEventListener('dragover', handleDragOver)
    document.removeEventListener('drop', handleDrop)
    document.removeEventListener('dragend', handleDragEnd)
  }

  onUnmounted(cleanup)

  return {
    isDragging,
    dragData,
    dragPosition,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    cleanup
  }
}

/**
 * 拖拽调整大小Hook
 */
export function useResize(options = {}) {
  const {
    onResizeStart,
    onResizing,
    onResizeEnd,
    minHeight = 30,
    grid = 15
  } = options

  const isResizing = ref(false)
  const resizeData = ref(null)

  function handleResizeStart(e, data) {
    if (e.button !== 0) return

    e.stopPropagation()
    isResizing.value = true
    resizeData.value = { ...data, startY: e.clientY, startHeight: data.height }

    document.body.classList.add('resizing')

    onResizeStart?.(e, data)

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.addEventListener('mouseleave', handleMouseUp)
  }

  function handleMouseMove(e) {
    if (!isResizing.value) return

    const deltaY = e.clientY - resizeData.value.startY
    let newHeight = resizeData.value.startHeight + deltaY

    // 网格吸附
    newHeight = Math.round(newHeight / grid) * grid
    newHeight = Math.max(newHeight, minHeight)

    resizeData.value.height = newHeight

    onResizing?.(e, { height: newHeight })
  }

  function handleMouseUp(e) {
    if (!isResizing.value) return

    isResizing.value = false
    document.body.classList.remove('resizing')

    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
    document.removeEventListener('mouseleave', handleMouseUp)

    onResizeEnd?.(e, { height: resizeData.value.height })
  }

  return {
    isResizing,
    resizeData,
    handleResizeStart
  }
}

/**
 * 拖拽创建新事件Hook
 */
export function useDragCreate(options = {}) {
  const {
    onCreateStart,
    onCreating,
    onCreateEnd,
    cellHeight = 64, // 单元格高度
    timeStep = 15 // 时间步长（分钟）
  } = options

  const isCreating = ref(false)
  const createData = ref(null)

  function handleCreateStart(e) {
    if (e.button !== 0) return

    const target = e.currentTarget
    const rect = target.getBoundingClientRect()

    const offsetY = e.clientY - rect.top
    const minutes = Math.round((offsetY / cellHeight) * 60 / timeStep) * timeStep
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60

    const time = `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`

    isCreating.value = true
    createData.value = {
      startX: e.clientX,
      startY: e.clientY,
      currentX: e.clientX,
      currentY: e.clientY,
      time,
      duration: timeStep
    }

    document.body.classList.add('creating-event')

    onCreateStart?.(e, createData.value)

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  function handleMouseMove(e) {
    if (!isCreating.value) return

    const deltaY = e.clientY - createData.value.startY
    const deltaMinutes = Math.round((deltaY / cellHeight) * 60 / timeStep) * timeStep
    const duration = Math.abs(deltaMinutes)

    createData.value.currentX = e.clientX
    createData.value.currentY = e.clientY
    createData.value.duration = Math.max(duration, timeStep)

    onCreating?.(e, createData.value)
  }

  function handleMouseUp(e) {
    if (!isCreating.value) return

    document.body.classList.remove('creating-event')

    const data = { ...createData.value }
    isCreating.value = false
    createData.value = null

    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)

    if (data.duration >= timeStep) {
      onCreateEnd?.(e, data)
    }
  }

  return {
    isCreating,
    createData,
    handleCreateStart
  }
}
